/**
 * AdminHome.jsx  —  Admin Dashboard  (mobile-friendly update)
 * Keeps all existing functionality.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { admin } from "../../services/adminApi";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Users,
  FolderOpen,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Activity,
  RefreshCw,
  X,
  Download,
  Search,
  ArrowRight,
  Clock,
  ShieldCheck,
  Zap,
  BarChart3,
} from "lucide-react";

/* ─── tokens ─────────────────────────────────────────────────────── */
const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber: "#111111",
  amberDark: "#333333",
  amberBg: "rgba(0,0,0,0.06)",
  amberBorder: "rgba(0,0,0,0.12)",
  dark: "#0c1118",
  white: "#ffffff",
  border: "#e8ecf0",
  muted: "#6b7280",
  mutedLight: "#9ca3af",
};

/* ─── inject once ────────────────────────────────────────────────── */
(function boot() {
  if (typeof document === "undefined") return;

  if (!document.getElementById("adh-fonts")) {
    const l = document.createElement("link");
    l.id = "adh-fonts";
    l.rel = "stylesheet";
    l.href =
      "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
    document.head.appendChild(l);
  }

  if (!document.getElementById("adh-css")) {
    const s = document.createElement("style");
    s.id = "adh-css";
    s.textContent = `
      @keyframes adh-fade  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      @keyframes adh-spin  { to{transform:rotate(360deg)} }
      @keyframes adh-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
      @keyframes adh-shimmer {
        0%   { background-position: -600px 0; }
        100% { background-position:  600px 0; }
      }

      .adh-card {
        background:#fff;
        border:1px solid #e8ecf0;
        border-radius:14px;
        transition:box-shadow .18s,border-color .18s;
        min-width:0;
      }

      .adh-kpi {
        display:flex; align-items:stretch;
        cursor:pointer; overflow:hidden;
        padding:0; border:none; text-align:left; width:100%;
        background:#fff; border:1px solid #e8ecf0; border-radius:14px;
        transition:box-shadow .18s, transform .18s, border-color .18s;
        min-width:0;
      }
      .adh-kpi:hover {
        box-shadow:0 8px 32px rgba(0,0,0,.09);
        border-color:rgba(0,0,0,0.20);
        transform:translateY(-2px);
      }
      .adh-kpi-accent {
        width:4px; flex-shrink:0;
        border-radius:14px 0 0 14px;
      }
      .adh-kpi-body {
        flex:1; padding:18px 20px;
        display:flex; align-items:center; justify-content:space-between; gap:12px;
        min-width:0;
      }
      .adh-kpi-left {
        display:flex; flex-direction:column; gap:6px; min-width:0; flex:1;
      }
      .adh-kpi-label {
        font-size:.7rem; letter-spacing:.04em;
        color:#6b7280; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
      }
      .adh-kpi-value {
        font-family:'DM Sans',sans-serif; font-size:1.9rem; font-weight:700; letter-spacing:.02em; color:#0c1118; line-height:1;
      }
      .adh-kpi-sub {
        font-size:.68rem; letter-spacing:.06em; color:#9ca3af;
        white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
      }
      .adh-kpi-right {
        display:flex; flex-direction:column; align-items:flex-end; gap:8px; flex-shrink:0;
      }
      .adh-kpi-icon {
        width:38px; height:38px; border-radius:10px; flex-shrink:0;
        display:flex; align-items:center; justify-content:center;
      }
      .adh-badge {
        display:inline-flex; align-items:center; gap:3px;
        padding:3px 8px; border-radius:20px;
        font-size:.6rem; font-weight:700; letter-spacing:.05em;
        white-space:nowrap;
      }

      .adh-tip {
        background:#0c1118 !important;
        border:1px solid rgba(255,255,255,.15) !important;
        border-radius:8px !important;
        padding:8px 12px !important;
        font-family:'DM Sans',sans-serif !important;
        font-size:.72rem !important;
        color:#c8dae4 !important;
      }
      .adh-tip .recharts-tooltip-label {
        color:#ffffff !important; font-weight:600; margin-bottom:4px;
      }

      .adh-action {
        display:flex; align-items:center; gap:12px;
        padding:14px 16px; text-decoration:none;
        background:#fff; border:1px solid #e8ecf0; border-radius:12px;
        transition:all .15s;
        min-width:0;
      }
      .adh-action:hover {
        border-color:rgba(0,0,0,0.20);
        background:rgba(0,0,0,0.02);
        box-shadow:0 4px 16px rgba(0,0,0,0.08);
        transform:translateY(-1px);
      }

      .adh-shimmer {
        background:linear-gradient(90deg,#f0f2f5 25%,#e4e6ea 50%,#f0f2f5 75%);
        background-size:600px 100%;
        animation:adh-shimmer 1.4s ease infinite;
        border-radius:8px;
      }

      .adh-drawer {
        position:fixed; top:0; right:0; bottom:0;
        width:min(920px,96vw);
        background:#fff; z-index:61;
        display:flex; flex-direction:column;
        box-shadow:-4px 0 48px rgba(0,0,0,.16);
        transition:transform .28s cubic-bezier(.4,0,.2,1);
      }
      .adh-drawer.closed { transform:translateX(100%); }
      .adh-drawer.open   { transform:translateX(0); }

      .adh-kpi-grid {
        display:grid;
        grid-template-columns:repeat(4,1fr);
        gap:14px;
      }
      .adh-chart-grid {
        display:grid;
        grid-template-columns:repeat(2,1fr);
        gap:14px;
      }
      .adh-bottom-grid {
        display:grid;
        grid-template-columns:1fr 340px;
        gap:14px;
        align-items:start;
      }
      .adh-action-grid {
        display:grid;
        grid-template-columns:repeat(4,1fr);
        gap:10px;
      }

      .adh-tr:hover { background:#f9fafb; }

      .adh-page-header {
        display:flex;
        align-items:flex-start;
        justify-content:space-between;
        flex-wrap:wrap;
        gap:12px;
      }

      .adh-header-actions {
        display:flex;
        align-items:center;
        gap:10px;
        flex-shrink:0;
        flex-wrap:wrap;
        justify-content:flex-end;
      }

      .adh-drawer-controls {
        display:flex;
        gap:10px;
        margin-bottom:18px;
        flex-wrap:wrap;
      }

      .adh-mobile-scroll {
        overflow-x:auto;
        -webkit-overflow-scrolling:touch;
      }

      @media (max-width: 1100px) {
        .adh-kpi-grid   { grid-template-columns:repeat(2,1fr); }
        .adh-action-grid{ grid-template-columns:repeat(2,1fr); }
      }

      @media (max-width: 768px) {
        .adh-page-header {
          flex-direction:column;
          align-items:stretch;
        }
        .adh-header-actions {
          width:100%;
          justify-content:space-between;
        }
        .adh-kpi-grid    { grid-template-columns:1fr; }
        .adh-chart-grid  { grid-template-columns:1fr; }
        .adh-bottom-grid { grid-template-columns:1fr; }
        .adh-action-grid { grid-template-columns:1fr 1fr; }

        .adh-kpi-body {
          padding:16px;
        }
        .adh-kpi-value {
          font-size:1.5rem !important;
        }

        .adh-drawer {
          width:100vw;
          max-width:100vw;
        }

        .adh-drawer-controls {
          flex-direction:column;
          align-items:stretch;
        }

        .adh-card-compact {
          padding:18px !important;
        }

        .adh-chart-wrap {
          min-height:220px;
        }
      }

      @media (max-width: 560px) {
        .adh-action-grid { grid-template-columns:1fr; }

        .adh-header-actions {
          flex-direction:column;
          align-items:stretch;
        }

        .adh-header-actions > * {
          width:100%;
        }

        .adh-kpi-right {
          align-items:flex-end;
        }

        .adh-kpi-icon {
          width:34px;
          height:34px;
        }

        .adh-drawer-head {
          padding:16px 16px 0 !important;
        }

        .adh-drawer-body {
          padding:16px !important;
        }

        .adh-table-cell {
          padding:10px 12px !important;
        }
      }
    `;
    document.head.appendChild(s);
  }
})();

/* ─── hooks ─────────────────────────────────────────────────────── */
function useIsMobile(breakpoint = 768) {
  const getValue = () =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false;

  const [isMobile, setIsMobile] = useState(getValue);

  useEffect(() => {
    const onResize = () => setIsMobile(getValue());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

/* ─── utils ─────────────────────────────────────────────────────── */
const BASE = process.env.REACT_APP_BACKEND_URL || "";
async function jget(path) {
  const r = await fetch(`${BASE}${path}`, { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
const pctStr = (v) => {
  const n = Number(v ?? 0);
  return `${n >= 0 ? "+" : ""}${n}%`;
};
function timeAgo(iso) {
  if (!iso) return "";
  const d = Math.max(0, Date.now() - new Date(iso));
  if (d < 60e3) return "Just now";
  if (d < 3600e3) return `${Math.floor(d / 60e3)}m ago`;
  if (d < 86400e3) return `${Math.floor(d / 3600e3)}h ago`;
  return `${Math.floor(d / 86400e3)}d ago`;
}

/* ═══════════════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════════════ */
export default function AdminHome() {
  const isMobile = useIsMobile();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userGrowth, setUserGrowth] = useState([]);
  const [projectMonthly, setProjectMonthly] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [drawer, setDrawer] = useState({
    open: false,
    type: null,
    scope: null,
  });

  async function loadMetrics() {
    setLoading(true);
    try {
      const [m, growth, activity, pm] = await Promise.all([
        admin.metrics(),
        admin.metricsUserGrowth({ months: 6 }),
        admin.recentActivity({ limit: 8 }),
        admin.projectsMonthly({ months: 6 }),
      ]);
      setMetrics(m);
      setUserGrowth(growth?.items || []);
      setRecentActivity(activity?.items || []);
      setProjectMonthly(pm?.items || []);
      setLastUpdated(new Date());
    } catch (e) {
      console.error("Metrics:", e);
    } finally {
      setLoading(false);
    }
  }

  async function loadHealth() {
    setHealthLoading(true);
    try {
      setHealth(await admin.systemHealth());
    } catch {
    } finally {
      setHealthLoading(false);
    }
  }

  useEffect(() => {
    loadMetrics();
    loadHealth();
    const mi = setInterval(loadMetrics, 5 * 60000);
    const hi = setInterval(loadHealth, 60000);
    return () => {
      clearInterval(mi);
      clearInterval(hi);
    };
  }, []);

  const projectsNorm = useMemo(
    () =>
      (projectMonthly || []).map((m) => ({
        month: m.month,
        Active: Number(m.active ?? m.actived ?? m.requested ?? 0),
        Archived: Number(m.archived ?? 0),
      })),
    [projectMonthly],
  );

  const growthData = useMemo(
    () =>
      (userGrowth || []).map((d) => ({
        month: d.month,
        Users: Number(d.users || 0),
      })),
    [userGrowth],
  );

  if (loading && !metrics) return <FullSkeleton />;

  const openDrawer = (type, scope) => setDrawer({ open: true, type, scope });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? 18 : 22,
        fontFamily: T.fontBody,
        animation: "adh-fade .25s ease",
        minWidth: 0,
      }}
    >
      <div className="adh-page-header">
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 24,
              fontWeight: 700,
              color: "#0c1118",
              lineHeight: 1.2,
              margin: 0,
            }}
          >
            Admin Dashboard
          </h1>
          {isMobile ? null : (
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                color: "#6b7280",
                margin: "4px 0 0",
              }}
            >
              Platform health and activity at a glance.
            </p>
          )}
        </div>

        <div className="adh-header-actions display-flex align-items-center gap-10 flex-shrink-0">
          <span
            style={{
              fontFamily: T.fontBody,
              fontSize: ".58rem",
              color: T.mutedLight,
              whiteSpace: "nowrap",
            }}
          >
            Updated {lastUpdated.toLocaleTimeString()}
          </span>

          <button
            onClick={loadMetrics}
            disabled={loading}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
              padding: "8px 16px",
              minHeight: 38,
              background: loading ? "#f3f4f6" : T.dark,
              color: loading ? T.mutedLight : T.white,
              border: "none",
              borderRadius: 8,
              fontFamily: T.fontBody,
              fontSize: ".82rem",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            <RefreshCw
              size={13}
              style={{
                animation: loading ? "adh-spin .8s linear infinite" : "none",
              }}
            />
            Refresh
          </button>
        </div>
      </div>

      <div className="adh-kpi-grid">
        <KpiCard
          label="Total Users"
          value={fmt(metrics?.totalUsers)}
          change={pctStr(metrics?.changes?.usersPct)}
          up={metrics?.changes?.usersPct >= 0}
          icon={<Users size={18} />}
          accent="#6366f1"
          onClick={() => openDrawer("users", "active")}
        />
        <KpiCard
          label="Active Projects"
          value={fmt(metrics?.activeProjects)}
          change={pctStr(metrics?.changes?.activeProjectsPct)}
          up={metrics?.changes?.activeProjectsPct >= 0}
          icon={<FolderOpen size={18} />}
          accent={T.amber}
          onClick={() => openDrawer("projects", "active")}
        />
        <KpiCard
          label="Archived Projects"
          value={fmt(metrics?.archivedProjects)}
          change={pctStr(metrics?.changes?.archivedProjectsPct)}
          up={metrics?.changes?.archivedProjectsPct >= 0}
          icon={<FolderOpen size={18} />}
          accent="#64748b"
          onClick={() => openDrawer("projects", "archived")}
        />
        <KpiCard
          label="Active Subscriptions"
          value={fmt(metrics?.activeSubscriptions)}
          change={pctStr(metrics?.changes?.activeSubscriptionsPct)}
          up={metrics?.changes?.activeSubscriptionsPct >= 0}
          icon={<CreditCard size={18} />}
          accent="#22c55e"
          onClick={() => openDrawer("subscriptions", "active")}
        />
      </div>

      <div className="adh-chart-grid">
        <div className="adh-card adh-card-compact" style={{ padding: "22px 20px 16px" }}>
          <CardHeader label="New User Signups" icon={<BarChart3 size={15} />} />
          <div className="adh-chart-wrap">
            <ResponsiveContainer width="100%" height={isMobile ? 220 : 220}>
              <AreaChart
                data={growthData}
                margin={{
                  top: 4,
                  right: isMobile ? 0 : 8,
                  bottom: 0,
                  left: isMobile ? -28 : -20,
                }}
              >
                <defs>
                  <linearGradient id="uGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.amber} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={T.amber} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f2f5"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tick={{
                    fontFamily: T.fontBody,
                    fontSize: 10,
                    fill: T.mutedLight,
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{
                    fontFamily: T.fontBody,
                    fontSize: 10,
                    fill: T.mutedLight,
                  }}
                  axisLine={false}
                  tickLine={false}
                  width={isMobile ? 28 : 36}
                />
                <Tooltip
                  contentStyle={{
                    background: T.dark,
                    border: `1px solid rgba(255,255,255,0.15)`,
                    borderRadius: 8,
                    fontFamily: T.fontBody,
                    fontSize: ".72rem",
                  }}
                  labelStyle={{
                    color: T.white,
                    fontWeight: 700,
                    marginBottom: 4,
                  }}
                  itemStyle={{ color: "#c8dae4" }}
                  cursor={{
                    stroke: "#111111",
                    strokeWidth: 1,
                    strokeDasharray: "4 2",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="Users"
                  stroke="#111111"
                  strokeWidth={2.5}
                  fill="url(#uGrad)"
                  dot={{ r: 4, fill: T.white, stroke: "#111111", strokeWidth: 2.5 }}
                  activeDot={{ r: 6, fill: "#111111" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="adh-card adh-card-compact" style={{ padding: "22px 20px 16px" }}>
          <CardHeader label="Project Activity" icon={<Activity size={15} />} />
          <div className="adh-chart-wrap">
            <ResponsiveContainer width="100%" height={isMobile ? 220 : 220}>
              <BarChart
                data={projectsNorm}
                margin={{
                  top: 4,
                  right: isMobile ? 0 : 8,
                  bottom: 0,
                  left: isMobile ? -28 : -20,
                }}
                barGap={3}
                barCategoryGap="30%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f2f5"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tick={{
                    fontFamily: T.fontBody,
                    fontSize: 10,
                    fill: T.mutedLight,
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{
                    fontFamily: T.fontBody,
                    fontSize: 10,
                    fill: T.mutedLight,
                  }}
                  axisLine={false}
                  tickLine={false}
                  width={isMobile ? 28 : 36}
                />
                <Tooltip
                  contentStyle={{
                    background: T.dark,
                    border: `1px solid rgba(255,255,255,0.15)`,
                    borderRadius: 8,
                    fontFamily: T.fontBody,
                    fontSize: ".72rem",
                  }}
                  labelStyle={{
                    color: T.white,
                    fontWeight: 700,
                    marginBottom: 4,
                  }}
                  itemStyle={{ color: "#c8dae4" }}
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{
                    fontFamily: T.fontBody,
                    fontSize: ".62rem",
                    letterSpacing: ".06em",
                    paddingTop: 12,
                  }}
                />
                <Bar dataKey="Active" fill="#111111" radius={[4, 4, 0, 0]} />
                <Bar
                  dataKey="Archived"
                  fill={T.dark}
                  radius={[4, 4, 0, 0]}
                  opacity={0.65}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="adh-bottom-grid">
        <div className="adh-card adh-card-compact" style={{ padding: 22 }}>
          <CardHeader label="Recent Activity" icon={<Clock size={15} />} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {recentActivity.length === 0 ? (
              <EmptyState
                icon={<Activity size={20} />}
                msg="No recent activity"
              />
            ) : (
              recentActivity.map((a) => (
                <ActivityRow key={a.id || a.at} a={a} />
              ))
            )}
          </div>
        </div>

        <div className="adh-card adh-card-compact" style={{ padding: 22 }}>
          <CardHeader label="System Health" icon={<ShieldCheck size={15} />} />
          <HealthPanel health={health} loading={healthLoading} />
        </div>
      </div>

      <div className="adh-card adh-card-compact" style={{ padding: 22 }}>
        <CardHeader
          label="Navigate"
          icon={<Zap size={15} />}
          eyebrow="Quick Actions"
        />
        <div className="adh-action-grid">
          {[
            {
              icon: <Users size={18} />,
              label: "Manage Users",
              sub: "Accounts & roles",
              to: "/admin/users",
              accent: "#6366f1",
            },
            {
              icon: <FolderOpen size={18} />,
              label: "Projects",
              sub: "Review submissions",
              to: "/admin/projects",
              accent: T.amber,
            },
            {
              icon: <CreditCard size={18} />,
              label: "Subscriptions",
              sub: "Plans & billing",
              to: "/admin/subscriptions",
              accent: "#22c55e",
            },
            {
              icon: <BarChart3 size={18} />,
              label: "Reports",
              sub: "Analytics & stats",
              to: "/admin/reports",
              accent: "#8b5cf6",
            },
          ].map((a) => (
            <ActionCard key={a.to} {...a} />
          ))}
        </div>
      </div>

      <MetricDrawer
        open={drawer.open}
        type={drawer.type}
        scope={drawer.scope}
        onClose={() => setDrawer({ open: false, type: null, scope: null })}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   KPI CARD
═══════════════════════════════════════════════════════════════════ */
function KpiCard({ label, value, change, up, icon, accent, onClick }) {
  return (
    <button className="adh-kpi" onClick={onClick}>
      <div className="adh-kpi-accent" style={{ background: accent }} />

      <div className="adh-kpi-body">
        <div className="adh-kpi-left">
          <span className="adh-kpi-label" style={{ fontFamily: T.fontBody }}>
            {label}
          </span>
          <span className="adh-kpi-value" style={{ fontFamily: T.fontBody }}>
            {value}
          </span>
          <span className="adh-kpi-sub" style={{ fontFamily: T.fontBody }}>
            vs last 30 days
          </span>
        </div>

        <div className="adh-kpi-right">
          <div
            className="adh-kpi-icon"
            style={{
              background: `${accent}15`,
              border: `1px solid ${accent}30`,
              color: accent,
            }}
          >
            {icon}
          </div>

          <span
            className="adh-badge"
            style={{
              background: up ? "rgba(34,197,94,.1)" : "rgba(239,68,68,.1)",
              color: up ? "#15803d" : "#b91c1c",
              fontFamily: T.fontBody,
            }}
          >
            {up ? (
              <TrendingUp size={10} style={{ flexShrink: 0 }} />
            ) : (
              <TrendingDown size={10} style={{ flexShrink: 0 }} />
            )}
            {change}
          </span>
        </div>
      </div>
    </button>
  );
}

function CardHeader({ label, icon, eyebrow }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {eyebrow && (
        <div
          style={{
            fontFamily: T.fontBody,
            fontSize: ".57rem",
            letterSpacing: ".16em",
            color: T.amber,
            marginBottom: 4,
          }}
        >
          {eyebrow}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <span
          style={{
            fontFamily: T.fontBody,
            fontSize: "14px",
            fontWeight: 600,
            color: T.dark,
            minWidth: 0,
          }}
        >
          {label}
        </span>
        <span style={{ color: T.mutedLight, flexShrink: 0 }}>{icon}</span>
      </div>
    </div>
  );
}

function ActionCard({ icon, label, sub, to, accent }) {
  return (
    <Link to={to} className="adh-action">
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 9,
          flexShrink: 0,
          background: `${accent}15`,
          border: `1px solid ${accent}30`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: accent,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: T.fontBody,
            fontWeight: 600,
            fontSize: ".845rem",
            color: T.dark,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: T.fontBody,
            fontSize: ".74rem",
            color: T.muted,
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {sub}
        </div>
      </div>
      <ArrowRight size={14} style={{ color: T.mutedLight, flexShrink: 0 }} />
    </Link>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ACTIVITY ROW
═══════════════════════════════════════════════════════════════════ */
function ActivityRow({ a }) {
  const cfg = {
    user_registered: { icon: <Users size={13} />, color: "#6366f1" },
    project_created: { icon: <FolderOpen size={13} />, color: "#16a34a" },
    project_updated: { icon: <FolderOpen size={13} />, color: "#333333" },
    subscription_changed: { icon: <CreditCard size={13} />, color: "#8b5cf6" },
  }[a.type] || { icon: <Activity size={13} />, color: T.muted };

  const text =
    {
      user_registered: `New user: ${a.user}`,
      project_created: `${a.user} created "${a.project || a.projectId}"`,
      project_updated: `${a.user} updated "${a.project || a.projectId}"`,
      subscription_changed: `${a.user} — subscription ${a.plan || a.status || "updated"}`,
    }[a.type] ||
    a.title ||
    "Activity";

  return (
    <div
      className="adh-tr"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 8,
        transition: "background .12s",
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          flexShrink: 0,
          background: `${cfg.color}14`,
          border: `1px solid ${cfg.color}28`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: cfg.color,
        }}
      >
        {cfg.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: T.fontBody,
            fontSize: ".82rem",
            color: "#111827",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </div>
        <div
          style={{
            fontFamily: T.fontBody,
            fontSize: ".57rem",
            letterSpacing: ".06em",
            color: T.mutedLight,
            marginTop: 1,
          }}
        >
          {timeAgo(a.at)}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SYSTEM HEALTH PANEL
═══════════════════════════════════════════════════════════════════ */
function HealthPanel({ health, loading }) {
  const rows = [
    {
      label: "Server Uptime",
      value: health?.uptime ?? (loading ? "…" : "—"),
      status: "healthy",
    },
    {
      label: "Database",
      value: health?.db ?? (loading ? "…" : "—"),
      status: health?.db === "Online" ? "healthy" : "warning",
    },
    {
      label: "API p95",
      value:
        health?.apiP95Ms != null ? `${health.apiP95Ms}ms` : loading ? "…" : "—",
      status:
        health?.apiP95Ms == null
          ? "unknown"
          : health.apiP95Ms >= 1000
            ? "critical"
            : health.apiP95Ms >= 500
              ? "warning"
              : "healthy",
    },
    {
      label: "Error Rate (5m)",
      value:
        health?.errorRatePct != null
          ? `${health.errorRatePct}%`
          : loading
            ? "…"
            : "—",
      status:
        health?.errorRatePct == null
          ? "unknown"
          : health.errorRatePct >= 5
            ? "critical"
            : health.errorRatePct >= 2
              ? "warning"
              : "healthy",
    },
    {
      label: "Sessions",
      value:
        health?.activeSessions != null
          ? fmt(health.activeSessions)
          : loading
            ? "…"
            : "—",
      status: "healthy",
    },
  ];

  const dot = {
    healthy: "#22c55e",
    warning: "#111111",
    critical: "#ef4444",
    unknown: "#9ca3af",
  };
  const vclr = {
    healthy: "#111827",
    warning: "#333333",
    critical: "#dc2626",
    unknown: T.mutedLight,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {rows.map((r) => (
        <div
          key={r.label}
          style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}
        >
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: dot[r.status] || "#9ca3af",
              flexShrink: 0,
              animation:
                r.status !== "healthy"
                  ? "adh-pulse 1.6s ease infinite"
                  : "none",
            }}
          />
          <span
            style={{
              fontFamily: T.fontBody,
              fontSize: ".82rem",
              color: "#374151",
              flex: 1,
              minWidth: 0,
            }}
          >
            {r.label}
          </span>
          <span
            style={{
              fontFamily: T.fontBody,
              fontSize: ".74rem",
              fontWeight: 700,
              color: vclr[r.status] || "#111827",
              letterSpacing: ".04em",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {r.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   METRIC DRAWER
═══════════════════════════════════════════════════════════════════ */
function MetricDrawer({ open, type, scope, onClose }) {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [drawerLoad, setDrawerLoad] = useState(false);
  const [q, setQ] = useState("");
  const [currentScope, setCurrentScope] = useState(scope || "active");
  const inputRef = useRef(null);

  useEffect(() => {
    setCurrentScope(scope || "active");
  }, [scope]);

  useEffect(() => {
    if (open && type) fetchRows();
  }, [open, type, currentScope]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 320);
  }, [open]);

  async function fetchRows() {
    const urlMap = {
      users: `/api/admin/metrics/users-list?status=${currentScope}`,
      projects: `/api/admin/metrics/projects-list?status=${currentScope}`,
      subscriptions: `/api/admin/metrics/subscriptions-list?status=${currentScope}`,
    };
    if (!urlMap[type]) return;
    setDrawerLoad(true);
    try {
      const r = await jget(urlMap[type]);
      setRows(r.items || []);
    } catch {
      setRows([]);
    } finally {
      setDrawerLoad(false);
    }
  }

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    return k
      ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(k))
      : rows;
  }, [rows, q]);

  function exportCsv() {
    if (!filtered.length) return;
    const hds = Object.keys(filtered[0]);
    const csv = [
      hds.join(","),
      ...filtered.map((row) =>
        hds
          .map((h) => {
            const v = row[h];
            const s =
              v == null
                ? ""
                : typeof v === "string"
                  ? v.replace(/"/g, '""')
                  : String(v);
            return `"${s}"`;
          })
          .join(","),
      ),
    ].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    a.download = `${type}-${currentScope}.csv`;
    a.click();
  }

  const titleMap = {
    users: "Users",
    projects: "Projects",
    subscriptions: "Subscriptions",
  };
  const scopeOpts = {
    users: ["active", "suspended", "all"],
    projects: ["active", "archived", "all"],
    subscriptions: ["active", "trialing", "all"],
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 60,
          background: "rgba(0,0,0,.5)",
          backdropFilter: "blur(3px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .25s",
        }}
      />

      <div className={`adh-drawer ${open ? "open" : "closed"}`}>
        <div
          style={{
            height: 3,
            background: `linear-gradient(90deg,#111111,#333333)`,
            flexShrink: 0,
          }}
        />

        <div
          className="adh-drawer-head"
          style={{
            padding: "18px 24px 0",
            flexShrink: 0,
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 14,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: T.fontBody,
                  fontSize: ".57rem",
                  letterSpacing: ".16em",
                  color: T.amber,
                  marginBottom: 4,
                }}
              >
                {currentScope}
              </div>
              <div
                style={{
                  fontFamily: T.fontBody,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: T.dark,
                }}
              >
                {titleMap[type] || "Details"}
              </div>
              {!drawerLoad && (
                <div
                  style={{
                    fontFamily: T.fontBody,
                    fontSize: ".57rem",
                    color: T.mutedLight,
                    marginTop: 3,
                  }}
                >
                  {filtered.length} records
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 8,
                cursor: "pointer",
                color: T.muted,
                display: "flex",
                transition: "all .15s",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = T.dark;
                e.currentTarget.style.color = T.dark;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.color = T.muted;
              }}
            >
              <X size={18} />
            </button>
          </div>

          {(scopeOpts[type] || []).length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 6,
                marginBottom: 14,
                flexWrap: "wrap",
              }}
            >
              {(scopeOpts[type] || []).map((s) => (
                <button
                  key={s}
                  onClick={() => setCurrentScope(s)}
                  style={{
                    padding: "5px 14px",
                    borderRadius: 20,
                    fontFamily: T.fontBody,
                    fontSize: ".62rem",
                    letterSpacing: ".08em",
                    cursor: "pointer",
                    transition: "all .15s",
                    background: currentScope === s ? T.dark : "transparent",
                    color: currentScope === s ? T.white : T.muted,
                    border:
                      currentScope === s
                        ? `1px solid ${T.dark}`
                        : "1px solid #e5e7eb",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="adh-drawer-controls">
            <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: T.mutedLight,
                }}
              />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search records…"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  paddingLeft: 34,
                  paddingRight: 12,
                  paddingTop: 9,
                  paddingBottom: 9,
                  fontFamily: T.fontBody,
                  fontSize: ".845rem",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  outline: "none",
                  background: "#f9fafb",
                  transition: "border-color .15s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#111111")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            <button
              onClick={exportCsv}
              disabled={!filtered.length}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                padding: "9px 16px",
                minHeight: 40,
                background: filtered.length ? T.dark : "#f3f4f6",
                color: filtered.length ? T.white : T.mutedLight,
                border: "none",
                borderRadius: 8,
                fontFamily: T.fontBody,
                fontSize: ".8rem",
                fontWeight: 600,
                cursor: filtered.length ? "pointer" : "not-allowed",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div
          className="adh-drawer-body"
          style={{ flex: 1, overflow: "auto", padding: "18px 24px" }}
        >
          {drawerLoad ? (
            <DrawerSkeleton />
          ) : type === "users" ? (
            <DataTable
              cols={["Name", "Email", "Role", "Plan", "Last Login", "Joined"]}
              rows={filtered}
              emptyIcon={<Users size={22} />}
              emptyMsg="No users found"
              renderRow={(u) => [
                <NameCell
                  initial={(u.name || u.email || "?")[0]}
                  label={u.name || "—"}
                />,
                <Muted>{u.email}</Muted>,
                <RolePill role={u.role} />,
                <PlanPill plan={u.plan} />,
                <MonoDate value={u.lastLoginAt} long />,
                <MonoDate value={u.createdAt} />,
              ]}
            />
          ) : type === "projects" ? (
            <DataTable
              cols={["Name", "Status", "Owner", "Updated", "Created"]}
              rows={filtered}
              emptyIcon={<FolderOpen size={22} />}
              emptyMsg="No projects found"
              renderRow={(p) => [
                <NameCell
                  initial={<FolderOpen size={13} />}
                  label={p.name || p.id}
                  iconBg="#f3f4f6"
                  iconColor="#6b7280"
                />,
                <StatusPill status={p.status} />,
                <Muted>{p.ownerEmail || "—"}</Muted>,
                <MonoDate value={p.updatedAt} long />,
                <MonoDate value={p.createdAt} />,
              ]}
            />
          ) : type === "subscriptions" ? (
            <DataTable
              cols={["User", "Plan", "Status", "Updated"]}
              rows={filtered}
              emptyIcon={<CreditCard size={22} />}
              emptyMsg="No subscriptions found"
              renderRow={(s) => [
                <NameCell
                  initial={(s.userEmail || "?")[0]}
                  label={s.userEmail || "—"}
                  iconBg="rgba(99,102,241,.1)"
                  iconColor="#6366f1"
                />,
                <PlanPill plan={s.plan} />,
                <StatusPill status={s.status} />,
                <MonoDate value={s.updatedAt} long />,
              ]}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

/* ─── Data table ────────────────────────────────────────────────── */
function DataTable({ cols, rows, renderRow, emptyIcon, emptyMsg }) {
  if (!rows.length) return <EmptyState icon={emptyIcon} msg={emptyMsg} />;
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div className="adh-mobile-scroll" style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            minWidth: 680,
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr
              style={{
                background: "#f9fafb",
                borderBottom: `1px solid ${T.border}`,
              }}
            >
              {cols.map((c) => (
                <th
                  key={c}
                  className="adh-table-cell"
                  style={{
                    padding: "10px 16px",
                    textAlign: "left",
                    fontFamily: T.fontBody,
                    fontSize: ".57rem",
                    letterSpacing: ".12em",
                    color: T.muted,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.id || i}
                className="adh-tr"
                style={{
                  borderBottom:
                    i < rows.length - 1 ? "1px solid #f3f4f6" : "none",
                  transition: "background .1s",
                }}
              >
                {renderRow(row).map((cell, j) => (
                  <td
                    key={j}
                    className="adh-table-cell"
                    style={{ padding: "11px 16px", verticalAlign: "middle" }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Table cell atoms ──────────────────────────────────────────── */
function NameCell({ initial, label, iconBg = "rgba(0,0,0,0.06)", iconColor = "#111111" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          flexShrink: 0,
          background: iconBg,
          border: `1px solid ${iconColor}28`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: T.fontBody,
          fontSize: ".9rem",
          color: iconColor,
        }}
      >
        {typeof initial === "string" ? initial.toUpperCase() : initial}
      </div>
      <span
        style={{
          fontFamily: T.fontBody,
          fontWeight: 500,
          fontSize: ".82rem",
          color: "#111827",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 180,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function Muted({ children }) {
  return (
    <span
      style={{ fontFamily: T.fontBody, fontSize: ".82rem", color: T.muted }}
    >
      {children}
    </span>
  );
}

function RolePill({ role }) {
  const c = {
    admin: { bg: "#ede9fe", color: "#7c3aed" },
    manager: { bg: "#dcfce7", color: "#15803d" },
  }[role] || { bg: "#f3f4f6", color: "#374151" };
  return (
    <span
      style={{
        display: "inline-flex",
        padding: "3px 9px",
        borderRadius: 20,
        fontFamily: T.fontBody,
        fontSize: ".6rem",
        letterSpacing: ".08em",
        background: c.bg,
        color: c.color,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {role || "user"}
    </span>
  );
}

function PlanPill({ plan }) {
  const paid = ["pro", "premium", "enterprise"].includes(plan);
  return (
    <span
      style={{
        display: "inline-flex",
        padding: "3px 9px",
        borderRadius: 20,
        fontFamily: T.fontBody,
        fontSize: ".6rem",
        letterSpacing: ".08em",
        fontWeight: 700,
        background: paid ? "rgba(0,0,0,0.06)" : "#f3f4f6",
        color: paid ? "#333333" : T.muted,
        border: paid ? `1px solid rgba(0,0,0,0.12)` : "1px solid transparent",
        whiteSpace: "nowrap",
      }}
    >
      {plan || "free"}
    </span>
  );
}

function StatusPill({ status }) {
  const c = {
    active: { bg: "#dcfce7", color: "#15803d", dot: "#22c55e" },
    trialing: { bg: "#dbeafe", color: "#1d4ed8", dot: "#3b82f6" },
    canceled: { bg: "#fee2e2", color: "#b91c1c", dot: "#ef4444" },
    archived: { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" },
    suspended: { bg: "#ffedd5", color: "#c2410c", dot: "#f97316" },
  }[status] || { bg: "#f3f4f6", color: T.muted, dot: "#9ca3af" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 20,
        fontFamily: T.fontBody,
        fontSize: ".6rem",
        letterSpacing: ".08em",
        fontWeight: 700,
        background: c.bg,
        color: c.color,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: c.dot,
          flexShrink: 0,
        }}
      />
      {status || "—"}
    </span>
  );
}

function MonoDate({ value, long }) {
  if (!value)
    return (
      <span
        style={{
          fontFamily: T.fontBody,
          fontSize: ".7rem",
          color: T.mutedLight,
          whiteSpace: "nowrap",
        }}
      >
        —
      </span>
    );
  const d = new Date(value);
  return (
    <span
      style={{
        fontFamily: T.fontBody,
        fontSize: ".68rem",
        color: T.muted,
        whiteSpace: "nowrap",
      }}
    >
      {long ? d.toLocaleString() : d.toLocaleDateString()}
    </span>
  );
}

function EmptyState({ icon, msg }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 24px",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.mutedLight,
        }}
      >
        {icon}
      </div>
      <span
        style={{
          fontFamily: T.fontBody,
          fontSize: ".845rem",
          color: T.mutedLight,
          textAlign: "center",
        }}
      >
        {msg}
      </span>
    </div>
  );
}

/* ─── Skeletons ─────────────────────────────────────────────────── */
function Sh({ w = "100%", h = 14, r = 7, style = {} }) {
  return (
    <div
      className="adh-shimmer"
      style={{ width: w, height: h, borderRadius: r, ...style }}
    />
  );
}

function FullSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Sh w={70} h={9} />
          <Sh w={240} h={34} />
          <Sh w={180} h={13} />
        </div>
        <Sh w={90} h={36} r={8} />
      </div>

      <div className="adh-kpi-grid">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            style={{
              background: "#fff",
              border: "1px solid #e8ecf0",
              borderRadius: 14,
              overflow: "hidden",
              display: "flex",
            }}
          >
            <div style={{ width: 4, background: "#e8ecf0" }} />
            <div
              style={{
                flex: 1,
                padding: "18px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minWidth: 0,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                <Sh w={80} h={10} />
                <Sh w={60} h={28} />
                <Sh w={100} h={10} />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  alignItems: "flex-end",
                }}
              >
                <Sh w={38} h={38} r={10} />
                <Sh w={52} h={20} r={20} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="adh-chart-grid">
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            style={{
              background: "#fff",
              border: "1px solid #e8ecf0",
              borderRadius: 14,
              padding: 22,
            }}
          >
            <Sh w="40%" h={18} style={{ marginBottom: 18 }} />
            <Sh w="100%" h={220} />
          </div>
        ))}
      </div>

      <div className="adh-bottom-grid">
        <div
          style={{
            background: "#fff",
            border: "1px solid #e8ecf0",
            borderRadius: 14,
            padding: 22,
          }}
        >
          <Sh w="35%" h={18} style={{ marginBottom: 18 }} />
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 10,
                alignItems: "center",
              }}
            >
              <Sh w={28} h={28} r={7} style={{ flexShrink: 0 }} />
              <Sh w="70%" h={14} />
            </div>
          ))}
        </div>
        <div
          style={{
            background: "#fff",
            border: "1px solid #e8ecf0",
            borderRadius: 14,
            padding: 22,
          }}
        >
          <Sh w="50%" h={18} style={{ marginBottom: 18 }} />
          {[...Array(5)].map((_, i) => (
            <Sh key={i} w="100%" h={16} style={{ marginBottom: 14 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DrawerSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="adh-shimmer"
          style={{ height: 44, borderRadius: 8, animationDelay: `${i * 55}ms` }}
        />
      ))}
    </div>
  );
}